import { createRouter, createWebHistory, type LocationQuery } from 'vue-router'
import { useSessionStore } from '@loczi/auth'

const adminUrl = (import.meta.env.VITE_ADMIN_URL ?? 'http://localhost:5175').replace(/\/$/, '')

/**
 * A tela única de login vive no app Admin — /login aqui só encaminha,
 * preservando o destino (return_to) e parâmetros extras (ex.: plan).
 */
export function redirectToAdminLogin(query: LocationQuery = {}): void {
  const { return_to, ...rest } = query
  const extra = new URLSearchParams()
  for (const [key, value] of Object.entries(rest)) {
    if (typeof value === 'string') extra.set(key, value)
  }
  let target = typeof return_to === 'string' && return_to.startsWith('/') && !return_to.startsWith('//')
    ? return_to
    : '/'
  const extraQs = extra.toString()
  if (extraQs) target += (target.includes('?') ? '&' : '?') + extraQs
  window.location.href = `${adminUrl}/login?return_to=${encodeURIComponent(target)}`
}

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Rota mantida para compatibilidade (links antigos, checkout do site);
    // o guard encaminha ao Admin antes de renderizar.
    { path: '/login', name: 'login', component: { render: () => null }, meta: { public: true } },
    { path: '/forgot-password', name: 'forgot-password', component: () => import('./views/ForgotPasswordView.vue'), meta: { public: true } },
    { path: '/reset-password', name: 'reset-password', component: () => import('./views/ResetPasswordView.vue'), meta: { public: true } },
    { path: '/select-account', name: 'select-account', component: () => import('./views/SelectAccountView.vue'), meta: { public: true } },
    // Autenticado sem conta nenhuma (ex.: cadastro via Google) — a única
    // saída é contratar no site; fica fora do shell (não há conta p/ contexto).
    { path: '/sem-servico', name: 'no-service', component: () => import('./views/NoServiceView.vue') },
    // Link público de posição — quem recebe não tem sessão nem conta.
    { path: '/s/:token', name: 'shared-link', component: () => import('./views/SharedLinkView.vue'), meta: { public: true } },
    // Aceite de convite: exige login (guard redireciona ao Admin e volta),
    // mas fica fora do shell para não depender de conta selecionada.
    { path: '/convite/:token', name: 'invite-accept', component: () => import('./views/InviteAcceptView.vue') },
    {
      path: '/',
      component: () => import('./layouts/AppShell.vue'),
      children: [
        // Home do app é o Mapa — ao abrir, cai direto nele.
        { path: '', redirect: '/mapa' },
        { path: 'mapa', name: 'map', component: () => import('./views/MapView.vue'), meta: { module: 'map' } },
        { path: 'visao-geral', name: 'overview', component: () => import('./views/OverviewView.vue'), meta: { module: 'overview' } },
        { path: 'dispositivos', name: 'devices', component: () => import('./views/DevicesView.vue'), meta: { module: 'devices' } },
        { path: 'celulares', name: 'celulares', component: () => import('./views/CelularesView.vue'), meta: { module: 'phones' } },
        { path: 'cercas', name: 'geofences', component: () => import('./views/GeofencesView.vue'), meta: { module: 'geofences' } },
        { path: 'alertas', name: 'alerts', component: () => import('./views/AlertsView.vue'), meta: { module: 'alerts' } },
        { path: 'historico', name: 'history', component: () => import('./views/HistoryView.vue'), meta: { titleKey: 'nav.history', module: 'history' } },
        { path: 'relatorios', name: 'reports', component: () => import('./views/ReportsView.vue'), meta: { titleKey: 'nav.reports', module: 'reports' } },
        { path: 'compartilhamento', name: 'sharing', component: () => import('./views/SharingView.vue'), meta: { titleKey: 'nav.sharing', module: 'sharing' } },
        { path: 'usuarios', name: 'users', component: () => import('./views/UsersView.vue'), meta: { titleKey: 'nav.users', module: 'users' } },
        { path: 'tags/:id', name: 'tag-detail', component: () => import('./views/TagDetailView.vue'), meta: { module: 'devices' } },
        { path: 'ativar', name: 'activate', component: () => import('./views/ActivateView.vue'), meta: { module: 'devices' } },
        { path: 'grupos', name: 'groups', component: () => import('./views/GroupsView.vue'), meta: { module: 'devices' } },
        { path: 'pedidos', name: 'orders', component: () => import('./views/OrdersView.vue') },
        { path: 'conta', name: 'account', component: () => import('./views/AccountView.vue'), meta: { module: 'account' } },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
})

router.beforeEach(async (to) => {
  // Login acontece só no Admin — esta rota nunca renderiza tela própria.
  if (to.name === 'login') {
    redirectToAdminLogin(to.query)
    return false
  }

  const session = useSessionStore()
  if (!session.ready) await session.bootstrap()

  if (to.meta.public) {
    if (session.isAuthenticated && to.name === 'select-account' && session.accounts.length === 0) {
      return { name: 'no-service' }
    }
    return true
  }

  if (!session.isAuthenticated) {
    redirectToAdminLogin({ return_to: to.fullPath })
    return false
  }
  // Sem conta nenhuma não há painel — leva à tela de contratação.
  if (session.accounts.length === 0) {
    return to.name === 'no-service' ? true : { name: 'no-service' }
  }
  if (!session.currentAccount && session.accounts.length > 0 && to.name !== 'select-account') {
    return { name: 'select-account' }
  }
  return true
})
